import type { AuditActor, AuditEntry } from '~/shared/utils/auditLog.js'

const COLLECTION_NOUNS: Record<string, string> = {
  members: 'member',
  subkrewes: 'SubKrewe',
  entries: 'dues entry',
  tasks: 'task',
  preapproved_members: 'pre-approval',
  products: 'product',
  lineups: 'lineup',
  system: 'settings',
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'admin',
  band_admin: 'band admin',
  redshirt_admin: 'redshirt admin',
  checkinVolunteer: 'check-in volunteer',
}

export interface AuditEntryDisplay {
  title: string
  actor: string
  /** Roles the actor held at the time, plus 'captain' when they were one. */
  actorRoles: string[]
  /** Field paths, readable: `approval_by_year › 2027 › status`. */
  fields: string[]
  /** Extra line for server actions (e.g. counts), if any. */
  detail?: string
}

export function describeAuditActor(actor: AuditActor | undefined): string {
  if (!actor) return 'Unknown'
  switch (actor.kind) {
    case 'user':
      return actor.name || `Member ${actor.uid?.slice(0, 8) ?? ''}`.trim()
    case 'service':
      return 'App server or Cloud Function'
    case 'system':
      return 'Google (automatic)'
    default:
      return actor.principal || 'Unknown'
  }
}

function actorRoles(actor: AuditActor | undefined): string[] {
  if (actor?.kind !== 'user') return []
  const roles = (actor.roles ?? []).map(role => ROLE_LABELS[role] ?? role)
  if (actor.memberType === 'captain') roles.push('captain')
  return roles
}

function formatField(path: string): string {
  return path.split('.').join(' › ')
}

/**
 * How one audit entry reads in a history list. `viewedPath` is the document
 * whose history is shown; changes to that document don't repeat its name.
 */
export function describeAuditEntry(entry: AuditEntry, viewedPath?: string): AuditEntryDisplay {
  const actor = describeAuditActor(entry.actor)
  const roles = actorRoles(entry.actor)

  if (entry.type === 'action') {
    const detail = entry.details
      ? Object.entries(entry.details).map(([key, value]) => `${key}: ${value}`).join(' · ')
      : undefined
    return { title: entry.summary, actor, actorRoles: roles, fields: [], ...(detail ? { detail } : {}) }
  }

  const noun = COLLECTION_NOUNS[entry.collection] ?? entry.collection
  const subject = entry.path === viewedPath ? '' : ` ${entry.label ? `${noun} ${entry.label}` : noun}`
  const verb = entry.op === 'create' ? 'Created' : entry.op === 'delete' ? 'Deleted' : 'Changed'
  const fields = entry.op === 'update' ? entry.fields.map(formatField) : []
  const count = entry.op === 'update' && !subject
    ? ` ${entry.fields.length} ${entry.fields.length === 1 ? 'field' : 'fields'}${entry.fieldsTruncated ? '+' : ''}`
    : ''

  return {
    title: `${verb}${subject || (entry.op === 'update' ? count : ' this record')}`,
    actor,
    actorRoles: roles,
    fields,
  }
}
